// AI Call Center translations (ar / fr / en) registered as a dedicated i18next
// namespace so they merge with the Firebase-loaded "translations" bundle without
// clobbering it and survive language switches. Use: useTranslation('aicallcenter').
import i18n from 'i18next';

const en = {
  // Header
  cc_title: 'AI Call Center',
  cc_pending: 'pending',
  cc_in_progress: 'in progress',
  cc_awaiting_customer: 'awaiting customer',
  // Tabs / search
  cc_active: 'Active',
  cc_accepted_tab: 'Accepted',
  cc_no_accepted: 'No accepted bookings',
  cc_history: 'History',
  cc_search_ph: 'Search by name or phone',
  cc_from: 'From',
  cc_to: 'To',
  cc_search: 'Search',
  cc_no_active: 'No active requests',
  cc_no_records: 'No records found',
  cc_unknown: 'Unknown',
  cc_select_request: 'Select a request from the queue',
  // Banners
  cc_banner_cancelled: '⚠ Request cancelled by customer. DO NOT book.',
  cc_banner_proposed: '📋 Booking proposal sent — waiting for customer confirmation',
  cc_proposed_at: 'Proposed at:',
  cc_banner_accepted: '✅ Customer accepted — booking is live',
  cc_booking_status: 'Booking status:',
  cc_from_label: 'From:',
  cc_to_label: 'To:',
  cc_driver: 'Driver:',
  cc_fare: 'Fare:',
  cc_ref: 'Ref:',
  cc_loading_booking: 'Loading booking…',
  cc_banner_declined: '❌ Customer declined the proposal',
  cc_banner_booking_cancelled: '❌ Booking was cancelled after customer accepted',
  cc_unknown_reason: 'unknown reason',
  cc_banner_agent_rejected: '❌ Rejected by agent',
  cc_banner_prebooking_cancelled: '🚫 Pre-booking cancelled by agent/admin',
  cc_cancelled_by: 'Cancelled by:',
  cc_banner_expired: '⏰ Request expired — no agent responded in time',
  cc_banner_proposal_expired: '⌛ Proposal expired — customer did not respond within 15 minutes',
  // Core info labels
  cc_customer: 'Customer',
  cc_phone: 'Phone',
  cc_status: 'Status',
  cc_created: 'Created',
  cc_handled_by: 'Handled by',
  cc_gps: 'GPS',
  cc_ai_understood: 'AI understood',
  cc_language: 'Language',
  cc_ai_from: 'From',
  cc_ai_to: 'To',
  cc_ai_reason: 'Reason',
  // Location warning
  cc_could_not_identify: '⚠ Customer could not identify:',
  cc_confirmed_pickup: 'Confirmed pickup:',
  cc_confirmed_drop: 'Confirmed drop:',
  // Proposed booking
  cc_proposed_booking: 'Proposed Booking',
  cc_type: 'Type:',
  cc_booking_id: 'Booking ID:',
  // Audio
  cc_customer_audio: '🎤 Customer Audio',
  cc_play_audio: '▶ Play customer audio',
  cc_loading_audio: 'Loading audio…',
  cc_audio_deleted: '🔇 Audio deleted after request closed',
  cc_audio_failed: '⚠ Audio was sent but could not be uploaded — read transcript below',
  // Messages
  cc_customer_messages: 'Customer messages',
  cc_transcription: 'Transcription',
  cc_agent_note: 'Agent note',
  // Action buttons
  cc_claim: 'Claim',
  cc_add_booking: 'Add Booking',
  cc_waiting_confirm: '⏳ Waiting for customer to confirm or decline…',
  cc_cancel_proposal: 'Cancel Proposal',
  cc_reject_request: 'Reject request',
  cc_ack_cancellation: 'Acknowledge cancellation',
  // Dialogs
  cc_reject_request_title: 'Reject Request',
  cc_reason: 'Reason',
  cc_note_for_customer: 'Note for customer (optional)',
  cc_back: 'Back',
  cc_reject: 'Reject',
  cc_ack_title: 'Acknowledge Cancellation',
  cc_note_optional: 'Note (optional)',
  cc_acknowledge: 'Acknowledge',
  // Toasts
  cc_toast_already_claimed: 'Already claimed by another agent.',
  cc_toast_claim_failed: 'Claim failed — network or permission error.',
  cc_toast_request_cancelled: 'Request cancelled.',
  cc_toast_audio_error: 'Could not load audio: ',
  cc_toast_history_error: 'Failed to load history: ',
  // Status chip labels
  cc_st_pending: 'Waiting',
  cc_st_claimed: 'In Progress',
  cc_st_proposed: 'Awaiting Customer',
  cc_st_accepted: 'Accepted',
  cc_st_rejected: 'Declined by Customer',
  cc_st_agent_rejected: 'Rejected by Agent',
  cc_st_expired: 'Expired',
  cc_st_missed: 'Missed',
  cc_st_cancelled_customer: 'Cancelled by Customer',
  cc_st_cancelled_agent: 'Cancelled by Agent',
  cc_st_cancelled_ack: 'Acknowledged',
  cc_st_proposal_expired: 'Proposal Expired',
  cc_st_booking_cancelled: 'Booking Cancelled',
  cc_st_booking_complete: 'Booking Complete',
  // Booking status labels
  cc_bk_new: 'Waiting for driver',
  cc_bk_accepted: 'Driver accepted',
  cc_bk_arrived: 'Driver arrived',
  cc_bk_started: 'Trip in progress',
  cc_bk_reached: 'Reached destination',
  cc_bk_paid: 'Payment complete',
  cc_bk_complete: 'Completed',
  cc_bk_cancelled: 'Cancelled',
  // Cancel reasons
  cc_cr_customer_issue: 'Customer issue',
  cc_cr_duplicate: 'Duplicate request',
  cc_cr_out_of_area: 'Out of service area',
  cc_cr_unable: 'Unable to fulfill',
  cc_cr_test: 'Test / fake request',
  cc_cr_other: 'Other',
};

const ar = {
  cc_title: 'مركز اتصال ساوي',
  cc_pending: 'قيد الانتظار',
  cc_in_progress: 'قيد المعالجة',
  cc_awaiting_customer: 'بانتظار الزبون',
  cc_active: 'النشطة',
  cc_accepted_tab: 'المقبولة',
  cc_no_accepted: 'لا توجد حجوزات مقبولة',
  cc_history: 'السجل',
  cc_search_ph: 'ابحث بالاسم أو الهاتف',
  cc_from: 'من',
  cc_to: 'إلى',
  cc_search: 'بحث',
  cc_no_active: 'لا توجد طلبات نشطة',
  cc_no_records: 'لا توجد سجلات',
  cc_unknown: 'غير معروف',
  cc_select_request: 'اختر طلباً من القائمة',
  cc_banner_cancelled: '⚠ ألغى الزبون الطلب. لا تقم بالحجز.',
  cc_banner_proposed: '📋 تم إرسال عرض الحجز — بانتظار تأكيد الزبون',
  cc_proposed_at: 'تم العرض في:',
  cc_banner_accepted: '✅ قبل الزبون — الحجز قيد التنفيذ',
  cc_booking_status: 'حالة الحجز:',
  cc_from_label: 'من:',
  cc_to_label: 'إلى:',
  cc_driver: 'السائق:',
  cc_fare: 'السعر:',
  cc_ref: 'المرجع:',
  cc_loading_booking: 'جارٍ تحميل الحجز…',
  cc_banner_declined: '❌ رفض الزبون العرض',
  cc_banner_booking_cancelled: '❌ أُلغي الحجز بعد قبول الزبون',
  cc_unknown_reason: 'سبب غير معروف',
  cc_banner_agent_rejected: '❌ رفضه الوكيل',
  cc_banner_prebooking_cancelled: '🚫 أُلغي الحجز المسبق من قبل الوكيل/المشرف',
  cc_cancelled_by: 'أُلغي بواسطة:',
  cc_banner_expired: '⏰ انتهت صلاحية الطلب — لم يستجب أي وكيل في الوقت المحدد',
  cc_banner_proposal_expired: '⌛ انتهت صلاحية العرض — لم يستجب الزبون خلال 15 دقيقة',
  cc_customer: 'الزبون',
  cc_phone: 'الهاتف',
  cc_status: 'الحالة',
  cc_created: 'أُنشئ في',
  cc_handled_by: 'تمت معالجته بواسطة',
  cc_gps: 'الموقع GPS',
  cc_ai_understood: 'فهم الذكاء الاصطناعي',
  cc_language: 'اللغة',
  cc_ai_from: 'من',
  cc_ai_to: 'إلى',
  cc_ai_reason: 'السبب',
  cc_could_not_identify: '⚠ لم يتمكن الزبون من تحديد:',
  cc_confirmed_pickup: 'نقطة الانطلاق المؤكدة:',
  cc_confirmed_drop: 'الوجهة المؤكدة:',
  cc_proposed_booking: 'الحجز المقترح',
  cc_type: 'النوع:',
  cc_booking_id: 'رقم الحجز:',
  cc_customer_audio: '🎤 صوت الزبون',
  cc_play_audio: '▶ تشغيل صوت الزبون',
  cc_loading_audio: 'جارٍ تحميل الصوت…',
  cc_audio_deleted: '🔇 تم حذف الصوت بعد إغلاق الطلب',
  cc_audio_failed: '⚠ تم إرسال الصوت لكن تعذّر رفعه — اقرأ النص أدناه',
  cc_customer_messages: 'رسائل الزبون',
  cc_transcription: 'النص المكتوب',
  cc_agent_note: 'ملاحظة الوكيل',
  cc_claim: 'استلام',
  cc_add_booking: 'إضافة حجز',
  cc_waiting_confirm: '⏳ بانتظار تأكيد الزبون أو رفضه…',
  cc_cancel_proposal: 'إلغاء العرض',
  cc_reject_request: 'رفض الطلب',
  cc_ack_cancellation: 'الإقرار بالإلغاء',
  cc_reject_request_title: 'رفض الطلب',
  cc_reason: 'السبب',
  cc_note_for_customer: 'ملاحظة للزبون (اختياري)',
  cc_back: 'رجوع',
  cc_reject: 'رفض',
  cc_ack_title: 'الإقرار بالإلغاء',
  cc_note_optional: 'ملاحظة (اختياري)',
  cc_acknowledge: 'إقرار',
  cc_toast_already_claimed: 'تم استلامه من قبل وكيل آخر.',
  cc_toast_claim_failed: 'فشل الاستلام — خطأ في الشبكة أو الصلاحية.',
  cc_toast_request_cancelled: 'تم إلغاء الطلب.',
  cc_toast_audio_error: 'تعذّر تحميل الصوت: ',
  cc_toast_history_error: 'فشل تحميل السجل: ',
  cc_st_pending: 'بالانتظار',
  cc_st_claimed: 'قيد المعالجة',
  cc_st_proposed: 'بانتظار الزبون',
  cc_st_accepted: 'مقبول',
  cc_st_rejected: 'رفضه الزبون',
  cc_st_agent_rejected: 'رفضه الوكيل',
  cc_st_expired: 'منتهي',
  cc_st_missed: 'فائت',
  cc_st_cancelled_customer: 'ألغاه الزبون',
  cc_st_cancelled_agent: 'ألغاه الوكيل',
  cc_st_cancelled_ack: 'تم الإقرار',
  cc_st_proposal_expired: 'انتهى العرض',
  cc_st_booking_cancelled: 'أُلغي الحجز',
  cc_st_booking_complete: 'اكتمل الحجز',
  cc_bk_new: 'بانتظار سائق',
  cc_bk_accepted: 'قبل السائق',
  cc_bk_arrived: 'وصل السائق',
  cc_bk_started: 'الرحلة جارية',
  cc_bk_reached: 'وصل إلى الوجهة',
  cc_bk_paid: 'تم الدفع',
  cc_bk_complete: 'مكتملة',
  cc_bk_cancelled: 'ملغاة',
  cc_cr_customer_issue: 'مشكلة لدى الزبون',
  cc_cr_duplicate: 'طلب مكرر',
  cc_cr_out_of_area: 'خارج نطاق الخدمة',
  cc_cr_unable: 'تعذّر التنفيذ',
  cc_cr_test: 'طلب تجريبي / وهمي',
  cc_cr_other: 'أخرى',
};

const fr = {
  cc_title: 'Centre d\'appels SAWI',
  cc_pending: 'en attente',
  cc_in_progress: 'en cours',
  cc_awaiting_customer: 'attente client',
  cc_active: 'Actives',
  cc_accepted_tab: 'Acceptées',
  cc_no_accepted: 'Aucune réservation acceptée',
  cc_history: 'Historique',
  cc_search_ph: 'Rechercher par nom ou téléphone',
  cc_from: 'Du',
  cc_to: 'Au',
  cc_search: 'Rechercher',
  cc_no_active: 'Aucune demande active',
  cc_no_records: 'Aucun enregistrement',
  cc_unknown: 'Inconnu',
  cc_select_request: 'Sélectionnez une demande dans la file',
  cc_banner_cancelled: '⚠ Demande annulée par le client. NE PAS réserver.',
  cc_banner_proposed: '📋 Proposition de réservation envoyée — en attente de confirmation du client',
  cc_proposed_at: 'Proposée à :',
  cc_banner_accepted: '✅ Le client a accepté — la réservation est active',
  cc_booking_status: 'Statut de la réservation :',
  cc_from_label: 'De :',
  cc_to_label: 'À :',
  cc_driver: 'Chauffeur :',
  cc_fare: 'Tarif :',
  cc_ref: 'Réf :',
  cc_loading_booking: 'Chargement de la réservation…',
  cc_banner_declined: '❌ Le client a refusé la proposition',
  cc_banner_booking_cancelled: '❌ La réservation a été annulée après acceptation du client',
  cc_unknown_reason: 'raison inconnue',
  cc_banner_agent_rejected: '❌ Rejetée par l\'agent',
  cc_banner_prebooking_cancelled: '🚫 Pré-réservation annulée par l\'agent/admin',
  cc_cancelled_by: 'Annulée par :',
  cc_banner_expired: '⏰ Demande expirée — aucun agent n\'a répondu à temps',
  cc_banner_proposal_expired: '⌛ Proposition expirée — le client n\'a pas répondu dans les 15 minutes',
  cc_customer: 'Client',
  cc_phone: 'Téléphone',
  cc_status: 'Statut',
  cc_created: 'Créée',
  cc_handled_by: 'Traitée par',
  cc_gps: 'GPS',
  cc_ai_understood: 'Compris par l\'IA',
  cc_language: 'Langue',
  cc_ai_from: 'De',
  cc_ai_to: 'À',
  cc_ai_reason: 'Raison',
  cc_could_not_identify: '⚠ Le client n\'a pas pu identifier :',
  cc_confirmed_pickup: 'Départ confirmé :',
  cc_confirmed_drop: 'Destination confirmée :',
  cc_proposed_booking: 'Réservation proposée',
  cc_type: 'Type :',
  cc_booking_id: 'N° de réservation :',
  cc_customer_audio: '🎤 Audio du client',
  cc_play_audio: '▶ Écouter l\'audio du client',
  cc_loading_audio: 'Chargement de l\'audio…',
  cc_audio_deleted: '🔇 Audio supprimé après la clôture de la demande',
  cc_audio_failed: '⚠ L\'audio a été envoyé mais n\'a pas pu être téléchargé — lisez la transcription ci-dessous',
  cc_customer_messages: 'Messages du client',
  cc_transcription: 'Transcription',
  cc_agent_note: 'Note de l\'agent',
  cc_claim: 'Prendre en charge',
  cc_add_booking: 'Ajouter une réservation',
  cc_waiting_confirm: '⏳ En attente de confirmation ou de refus du client…',
  cc_cancel_proposal: 'Annuler la proposition',
  cc_reject_request: 'Rejeter la demande',
  cc_ack_cancellation: 'Accuser réception de l\'annulation',
  cc_reject_request_title: 'Rejeter la demande',
  cc_reason: 'Raison',
  cc_note_for_customer: 'Note pour le client (facultatif)',
  cc_back: 'Retour',
  cc_reject: 'Rejeter',
  cc_ack_title: 'Accuser réception de l\'annulation',
  cc_note_optional: 'Note (facultatif)',
  cc_acknowledge: 'Accuser réception',
  cc_toast_already_claimed: 'Déjà prise en charge par un autre agent.',
  cc_toast_claim_failed: 'Échec de la prise en charge — erreur réseau ou de permission.',
  cc_toast_request_cancelled: 'Demande annulée.',
  cc_toast_audio_error: 'Impossible de charger l\'audio : ',
  cc_toast_history_error: 'Échec du chargement de l\'historique : ',
  cc_st_pending: 'En attente',
  cc_st_claimed: 'En cours',
  cc_st_proposed: 'Attente client',
  cc_st_accepted: 'Acceptée',
  cc_st_rejected: 'Refusée par le client',
  cc_st_agent_rejected: 'Rejetée par l\'agent',
  cc_st_expired: 'Expirée',
  cc_st_missed: 'Manquée',
  cc_st_cancelled_customer: 'Annulée par le client',
  cc_st_cancelled_agent: 'Annulée par l\'agent',
  cc_st_cancelled_ack: 'Accusé reçu',
  cc_st_proposal_expired: 'Proposition expirée',
  cc_st_booking_cancelled: 'Réservation annulée',
  cc_st_booking_complete: 'Réservation terminée',
  cc_bk_new: 'En attente d\'un chauffeur',
  cc_bk_accepted: 'Chauffeur accepté',
  cc_bk_arrived: 'Chauffeur arrivé',
  cc_bk_started: 'Course en cours',
  cc_bk_reached: 'Destination atteinte',
  cc_bk_paid: 'Paiement effectué',
  cc_bk_complete: 'Terminée',
  cc_bk_cancelled: 'Annulée',
  cc_cr_customer_issue: 'Problème client',
  cc_cr_duplicate: 'Demande en double',
  cc_cr_out_of_area: 'Hors zone de service',
  cc_cr_unable: 'Impossible à traiter',
  cc_cr_test: 'Demande test / fictive',
  cc_cr_other: 'Autre',
};

// Register into a dedicated namespace for all three languages (deep merge, overwrite).
// Called from within the AiCallCenter component (after i18n.init() has run).
// Do NOT call at module level — i18n.store is null before init and throws.
let _registered = false;
export function registerCCTranslations() {
  if (_registered) return;
  _registered = true;
  i18n.addResourceBundle('en', 'aicallcenter', en, true, true);
  i18n.addResourceBundle('ar', 'aicallcenter', ar, true, true);
  i18n.addResourceBundle('fr', 'aicallcenter', fr, true, true);
}

export default { en, ar, fr };
